import api from '~/services/api';
import { Fornecedor, Produto, Loja, Tributacao } from '../../protocols';

const DEBUG_PREFIX = '[EntradaNFE][buscaTributacao]';

const snapshotTributacao = (tributacao?: Tributacao) =>
  tributacao
    ? {
        cod_tributacao: tributacao.cod_tributacao,
        des_tributacao: tributacao.des_tributacao,
        tipo_tributacao: tributacao.tipo_tributacao,
        cod_situacao_tributaria: tributacao.cod_situacao_tributaria,
        val_icms: tributacao.val_icms,
        val_reducao: tributacao.val_reducao,
      }
    : null;

export async function buscaTributacao(
  loja: Loja,
  fornecedor: Fornecedor,
  produto: Produto,
  tributacoes: Tributacao[],
): Promise<number> {
  const identificacao = `produto=${produto.cod_produto} | NCM=${produto.cod_ncm}`;
  let codTributacaoFinal: number | undefined;
  let origemResultado = 'não definida';

  console.groupCollapsed(`${DEBUG_PREFIX} INÍCIO | ${identificacao}`);
  console.log(`${DEBUG_PREFIX} 01 - Parâmetros recebidos`, {
    origem: 'EntradaNFEContent.prepareItemForInsertion',
    loja: { des_uf: loja.des_uf, uf: loja.uf },
    fornecedor: {
      des_uf: fornecedor.des_uf,
      flg_regime_st: fornecedor.flg_regime_st,
    },
    produto: {
      cod_produto: produto.cod_produto,
      des_produto: produto.des_produto,
      cod_ncm: produto.cod_ncm,
      cod_tipo_item: produto.cod_tipo_item,
      cod_tributacao: produto.cod_tributacao,
      cod_trib_entrada: produto.cod_trib_entrada,
      tipo_tributacao: produto.tipo_tributacao,
    },
    quantidade_tributacoes: tributacoes.length,
  });

  try {
    if (loja.des_uf !== fornecedor.des_uf) {
      let codTributacao = 0;

      const params = {
        cod_ncm: produto.cod_ncm,
        des_sigla: fornecedor.des_uf,
        cod_tipo_item: produto.cod_tipo_item,
      };

      console.log(`${DEBUG_PREFIX} 02 - Operação interestadual`, {
        loja_uf: loja.des_uf,
        fornecedor_uf: fornecedor.des_uf,
        proxima_origem: 'GET /entrada-nfe/tributacao-fornecedor',
      });
      console.log(`${DEBUG_PREFIX} 03 - Parâmetros enviados à API`, params);

      const res = await api.get('/entrada-nfe/tributacao-fornecedor', {
        params,
      });

      const { success, data } = res.data;

      console.log(`${DEBUG_PREFIX} 04 - Resposta da API recebida`, {
        http_status: res.status,
        success,
        data,
        cod_tributacao_retornado: data?.cod_tributacao,
      });

      if (!success || !data) {
        codTributacao = produto.cod_trib_entrada;
        origemResultado =
          'fallback produto.cod_trib_entrada (API sem sucesso ou sem data)';
        console.warn(`${DEBUG_PREFIX} 05 - Fallback aplicado`, {
          motivo: !success ? 'success=false' : 'data ausente',
          cod_trib_entrada_produto: produto.cod_trib_entrada,
        });
      } else {
        codTributacao = data.cod_tributacao;
        origemResultado = 'API /entrada-nfe/tributacao-fornecedor';
        console.log(`${DEBUG_PREFIX} 05 - Código adotado da API`, {
          cod_tributacao: codTributacao,
        });
      }

      if (fornecedor.flg_regime_st) {
        console.log(`${DEBUG_PREFIX} 06 - Avaliação do regime ST`, {
          fornecedor_flg_regime_st: fornecedor.flg_regime_st,
          produto_tipo_tributacao: produto.tipo_tributacao,
          regra_aplicavel: produto.tipo_tributacao === 3,
        });
        if (produto.tipo_tributacao === 3) {
          const tributacaoEntrada = tributacoes.find(
            (tributacao: Tributacao) =>
              tributacao.cod_tributacao === codTributacao,
          );
          console.log(`${DEBUG_PREFIX} 07 - Busca do código atual na lista`, {
            criterio: { cod_tributacao: codTributacao },
            encontrado: Boolean(tributacaoEntrada),
            tributacao: snapshotTributacao(tributacaoEntrada),
          });
          if (tributacaoEntrada) {
            const perICMSEnt = tributacaoEntrada.val_icms;
            const perRedEnt = tributacaoEntrada.val_reducao;
            const filteredTributacao = tributacoes.find(
              (tributacao: Tributacao) => {
                return (
                  tributacao.val_icms === perICMSEnt &&
                  tributacao.val_reducao === perRedEnt &&
                  tributacao.tipo_tributacao === 3
                );
              },
            );
            console.log(
              `${DEBUG_PREFIX} 08 - Busca da tributação ST equivalente`,
              {
                criterio: {
                  val_icms: perICMSEnt,
                  val_reducao: perRedEnt,
                  tipo_tributacao: 3,
                },
                encontrado: Boolean(filteredTributacao),
                tributacao: snapshotTributacao(filteredTributacao),
              },
            );
            if (filteredTributacao) {
              codTributacao = filteredTributacao.cod_tributacao;
              origemResultado = 'tributação ST equivalente na lista local';
              console.log(
                `${DEBUG_PREFIX} 09 - Código substituído pela regra ST`,
                {
                  cod_tributacao: codTributacao,
                },
              );
            } else {
              console.warn(
                `${DEBUG_PREFIX} 09 - Equivalente ST não encontrada`,
                {
                  cod_tributacao_mantido: codTributacao,
                },
              );
            }
          } else {
            console.warn(
              `${DEBUG_PREFIX} 08 - Código atual ausente na lista local`,
              {
                cod_tributacao_mantido: codTributacao,
              },
            );
          }
        }
      } else {
        console.log(`${DEBUG_PREFIX} 06 - Regra de regime ST não aplicável`, {
          fornecedor_flg_regime_st: fornecedor.flg_regime_st,
        });
      }
      // fora do estado
      codTributacaoFinal = codTributacao;
      return codTributacao;
    }

    console.log(`${DEBUG_PREFIX} 02 - Operação dentro do estado`, {
      loja_uf: loja.des_uf,
      fornecedor_uf: fornecedor.des_uf,
      api_consultada: false,
    });

    if (produto.cod_trib_entrada !== 0) {
      codTributacaoFinal = produto.cod_trib_entrada;
      origemResultado = 'produto.cod_trib_entrada';
      console.log(`${DEBUG_PREFIX} 03 - Código de entrada do produto adotado`, {
        cod_tributacao: codTributacaoFinal,
      });
      return produto.cod_trib_entrada;
    }

    codTributacaoFinal = produto.cod_tributacao;
    origemResultado = 'produto.cod_tributacao (fallback dentro do estado)';
    console.log(`${DEBUG_PREFIX} 03 - Tributação padrão do produto adotada`, {
      motivo: 'produto.cod_trib_entrada é zero',
      cod_tributacao: codTributacaoFinal,
    });
    return produto.cod_tributacao;
  } catch (error) {
    origemResultado = 'erro durante a busca';
    console.error(`${DEBUG_PREFIX} ERRO | ${identificacao}`, {
      error,
      cod_tributacao_ate_o_erro: codTributacaoFinal,
    });
    throw error;
  } finally {
    console.log(`${DEBUG_PREFIX} FIM | ${identificacao}`, {
      cod_tributacao_final: codTributacaoFinal,
      origem_resultado: origemResultado,
    });
    console.groupEnd();
  }
}
